import UserVerifiedStatus from '@features/workspace/components/users/components/user-verification-status';
import {
  ILabelValueProp,
  LabelValueList,
} from '@features/workspace/components/label-value-list';
import { FC, useState } from 'react';
import {
  EmployeePositionLabelEnum,
  DocumentTypeEnum,
  JumioVerificationStatusEnum,
} from '@utils/constants';
import moment from 'moment';
import { Link, Box, Tooltip, Select, MenuItem } from '@mui/material';
import { store } from '@app/store';
import { setNotification } from '@store/features/notification';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
  reassignEmployee,
  sendIDVerificationEmail,
} from '@features/workspace/store/actions';
import InfoIcon from '@mui/icons-material/Info';
import InlineLoader from '@components/inline-loader';
import { selectActiveCompanyId } from '@features/workspace/store/selectors';
import { fetchCompanyUsers } from '@features/workspace/store/actions/fetch';
import ConfirmDialog from '@features/workspace/components/dialogs/confirm-dialog';
import { sendResetPasswordEmail } from '@features/workspace/store/actions/update';
import config from '@/app/configs/config';

type UserViewProps = {
  user: any;
  bankingMode?: boolean;
};

const UserView: FC<UserViewProps> = ({ user, bankingMode = false }) => {
  const dispatch = useAppDispatch();
  const companyId = useAppSelector(selectActiveCompanyId);
  const isVerified = user?.person?.identityDocuments?.[0]?.isVerified;
  const [isVerificationLoading, setIsVerificationLoading] =
    useState<any>(false);
  const [isPasswordResetLoading, setIsPasswordResetLoading] = useState(false);
  const [isVerificationSent, setIsVerificationSent] = useState<any>(false);
  const addressDocument = user.documents.find(
    (doc: any) =>
      doc.userId === user.id &&
      doc.documentTypeEnum === DocumentTypeEnum.PersonalAddressRegistration
  );
  const [position, setPosition] = useState(user?.assignment?.position);
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);

  const onSendVerification = async () => {
    setIsVerificationLoading(true);
    await dispatch(sendIDVerificationEmail({ userId: user.id, companyId }));
    setIsVerificationSent(true);
    setIsVerificationLoading(false);
    store.dispatch(
      setNotification({
        message: `ID verification link has been sent to ${[
          user?.person?.firstName,
          user?.person?.lastName,
        ].join(' ')} by email`,
        variant: 'success',
      })
    );
  };

  const resetPassword = async () => {
    setIsPasswordResetLoading(true);
    try {
      await dispatch(sendResetPasswordEmail({ userId: user.id }));
      store.dispatch(
        setNotification({
          message: `A reset link has been sent to ${user?.person?.firstName} ${user?.person?.lastName}`,
          variant: 'success',
        })
      );
    } catch (e) {
      store.dispatch(
        setNotification({
          message: 'Reset link could not be sent. Please try again',
          variant: 'error',
        })
      );
    } finally {
      setIsPasswordResetLoading(false);
      setResetPasswordModalOpen(false);
    }
  };

  const getVerificationLinkHint = () => {
    if (isVerified && isVerificationSent) {
      return 'ID document is verified and new verification link has been sent';
    }

    if (isVerified) {
      return 'ID document is verified';
    }

    if (isVerificationSent) {
      return 'Verification link has been sent';
    }

    return null;
  };

  const verificationLinkHint = getVerificationLinkHint();

  const verificationLink = (
    <Box alignItems="center" display="flex">
      <Link
        sx={{
          cursor: 'pointer',
          textDecoration: 'underline',
        }}
        onClick={onSendVerification}
      >
        Send client link
      </Link>
      {verificationLinkHint && (
        <Tooltip title={verificationLinkHint}>
          <Box display="flex" ml={1}>
            <InfoIcon fontSize="small" />
          </Box>
        </Tooltip>
      )}
    </Box>
  );

  const passwordResetLink = (
    <Box alignItems="center" display="flex">
      <Link
        sx={{
          cursor: 'pointer',
          textDecoration: 'underline',
        }}
        onClick={() => setResetPasswordModalOpen(true)}
      >
        Send reset link
      </Link>
    </Box>
  );

  const onPositionChange = async (newPosition: number) => {
    if (companyId) {
      setPosition(newPosition);
      await dispatch(
        reassignEmployee({
          companyId,
          userId: user.id,
          position: newPosition,
        })
      );
      dispatch(
        setNotification({
          // eslint-disable-next-line quotes
          message: "User's position updated",
          variant: 'success',
        })
      );
      dispatch(fetchCompanyUsers(null));
    }
  };

  const positionSelector = (
    <Box display="flex" width={0.7}>
      <Select
        fullWidth
        label="Company role"
        size="small"
        value={position}
        onChange={(event) => onPositionChange(event.target.value)}
      >
        {Object.entries(EmployeePositionLabelEnum)
          .filter(([value, label]) => Number.isNaN(Number(label)))
          .map(([value, label]) => (
            <MenuItem key={`user-${user.id}-position-${value}`} value={value}>
              {label}
            </MenuItem>
          ))}
      </Select>
    </Box>
  );

  const properties: Array<ILabelValueProp> = [
    { label: 'Title', value: user?.person?.title || null },
    { label: 'First name', value: user?.person?.firstName || null },
    { label: 'Last name', value: user?.person?.lastName || null },
    {
      label: 'ID number',
      value: user?.person?.identityDocuments?.[0]?.idNumber || null,
    },
    { label: 'Unique customer ID', value: user?.id },
    {
      label: 'Company role',
      value: !bankingMode
        ? EmployeePositionLabelEnum[user?.assignment?.position] || null
        : positionSelector,
    },
    {
      label: 'Verification',
      value: (
        <UserVerifiedStatus
          addressVerificationDate={
            addressDocument
              ? moment(addressDocument?.date).format(config.dateFormat)
              : null
          }
          isAddressVerified={
            user?.address?.jumioVerificationState ===
            JumioVerificationStatusEnum.Approved
          }
          isVerified={user?.person?.identityDocuments?.[0]?.isVerified}
          jumioLink={user?.assignment?.identityDocumentVerificationTrackLink}
          verificationDate={null}
        />
      ),
    },
    { label: 'User number', value: user?.phoneNumber || null },
    { label: 'Email address', value: user?.email } || null,
    {
      label: 'Password reset link',
      value: !isPasswordResetLoading ? passwordResetLink : <InlineLoader />,
    },
    {
      label: 'PEP',
      title: 'Politically exposed person',
      value: user?.person?.isPep ? 'Yes' : 'No',
    },
    {
      label: 'Send ID verification link',
      value: !isVerificationLoading ? verificationLink : <InlineLoader />,
    },
  ];

  if (user?.isBlocked) {
    properties.push({ label: 'Blacklist', value: user?.blockReason ?? '' });
  }

  return (
    <>
      <ConfirmDialog
        content={`You’re about to send a password reset link to ${user?.person?.firstName} ${user?.person?.lastName}. Are you sure you want to proceed?`}
        open={resetPasswordModalOpen}
        title="Send password reset link"
        onCancel={() => setResetPasswordModalOpen(false)}
        onClose={() => setResetPasswordModalOpen(false)}
        onConfirm={resetPassword}
      />
      <LabelValueList properties={properties} />
    </>
  );
};

export default UserView;
